import { computed, ref, watch } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { usePermissions } from '@/composables/usePermissions';
import { useMeQuery } from '@/composables/useMeQuery';
import { useMeV3Query } from '@/composables/useMeV3Query';
import { useMainStore } from '@/stores/mainStore';
import { useUserStore } from '@/stores/userStore';
import type { PermissionDraftMap } from '@/types/permissionModules';
import {
  createEmptyPermissionsGrants,
  type PermissionsGrants,
} from '@/types/permissionsGrants';
import { grantsFromDraft, mapMeV3Grants } from '@/utils/mapMeV3Grants';
import { reportUnknownGrants } from '@/utils/permissionGrantsV3';

type GrantKey = keyof PermissionsGrants;

// Legacy (v2) base grants by role. Admin is not Viewer under strict role equality,
// so Admin explicitly receives the viewer-or-admin grants as well.
const LEGACY_APP_ACCESS_GRANTS = [
  'viewAgents',
  'viewEarningsBenchmarks',
] as const satisfies readonly GrantKey[];

const LEGACY_VIEWER_OR_ADMIN_GRANTS = [
  'viewInstitutionalNil',
  'viewThirdPartyNil',
  'viewScholarships',
] as const satisfies readonly GrantKey[];

const LEGACY_ADMIN_GRANTS = [
  'editInstitutionalNil',
  'editThirdPartyNil',
  'editScholarships',
  'viewRevShareAgreements',
  'editRevShareAgreements',
  'viewScholarshipAgreements',
  'editScholarshipAgreements',
  'viewPayments',
  'editAgents',
  'editRoster',
] as const satisfies readonly GrantKey[];

function applyGrantList(grants: PermissionsGrants, keys: readonly GrantKey[]) {
  for (const key of keys) {
    grants[key] = true;
  }
}

/**
 * Internal Pinia store for the current user's permission grants.
 * Components must not import this directly — use the public
 * `usePermissionsStore` composable in `@/composables/usePermissionsStore`.
 */
export const usePermissionsStore = defineStore('permissions', () => {
  const userStore = useUserStore();
  const { data: meData } = useMeQuery();
  const {
    isTeamAdmin,
    isTeamViewer,
    canManagePayments,
  } = usePermissions();

  // Assigned-team questions read me.teams, not permissions — the two diverge on NO_ACCESS teams.
  const hasAppAccess = computed(() => (meData.value?.teams?.length ?? 0) > 0);

  /**
   * Legacy v2 fallback — preserves today's Admin/Viewer behavior exactly.
   * Roles come from usePermissions(); base grants are applied from the role lists,
   * then derived grants use the fallback formulas below (v3 will use different ones).
   */
  const legacyGrants = computed<PermissionsGrants>(() => {
    const grants = createEmptyPermissionsGrants();
    const isSuperAdmin = !!userStore.user?.isSuperAdmin;
    grants.isSuperAdmin = isSuperAdmin;

    if (!hasAppAccess.value) {
      return grants;
    }

    grants.hasAppAccess = true;

    const admin = !!isTeamAdmin.value;
    const viewer = !!isTeamViewer.value;

    // Base grants (role → grant lists)
    applyGrantList(grants, LEGACY_APP_ACCESS_GRANTS);

    if (viewer || admin) {
      applyGrantList(grants, LEGACY_VIEWER_OR_ADMIN_GRANTS);
    }

    if (admin) {
      applyGrantList(grants, LEGACY_ADMIN_GRANTS);
      // Payments EDIT is gated on can_manage_payments — not a blanket Admin grant.
      grants.editPayments = !!canManagePayments.value;
    }

    // Derived grants (fallback formulas). v3 uses different formulas inside mapMeV3Grants.

    // Nothing gates Total Athlete Earnings today (TeamBudgetTables.vue).
    grants.canViewTotalEarnings = hasAppAccess.value;

    // TeamRoster.vue short-circuits the page for non-admins.
    grants.viewRoster = admin;

    // Matches canAccessAdminRoutesWithSuperAdmin and SettingsNotifications' in-view isAdmin.
    const settingsPage = admin || isSuperAdmin;
    grants.viewSettingsScholarships = settingsPage;
    grants.viewContractTemplates = settingsPage;
    grants.viewSettingsNotifications = settingsPage;

    // Super Admin alone. v3 applies the same check, then short-circuits inside the mapper.
    grants.viewUserPermissions = isSuperAdmin;

    return grants;
  });

  const { data: meV3 } = useMeV3Query();
  const { teamTwrn } = storeToRefs(useMainStore());

  const mappedV3 = computed(() => mapMeV3Grants(meV3.value, teamTwrn.value));

  // Bootstrap caches v3 /me before this store exists, so the first run has to
  // see unknowns already in that payload. An empty list is a no-op inside reportUnknownGrants.
  watch(
    () => mappedV3.value.unknown.join('\0'),
    () => {
      const userTwrn = meV3.value?.user.twrn;
      if (!userTwrn) return;
      reportUnknownGrants(userTwrn, mappedV3.value.unknown);
    },
    { immediate: true },
  );

  /**
   * Temporary dev override (GM-3801). The mock drawer writes a draft here.
   * `v3Grants` prefers it over the `/me` mapper. It is not persisted, so a reload clears it.
   * Remove this in the permissions cleanup milestone.
   */
  const v3GrantsOverride = ref<PermissionDraftMap | null>(null);

  const v3Grants = computed(() => {
    const draft = v3GrantsOverride.value;
    if (!draft) return mappedV3.value.grants;

    const activeTeamTwrn = teamTwrn.value;
    const teams = meV3.value?.teams ?? [];

    return grantsFromDraft(draft, {
      isSuperAdmin: !!meV3.value?.user?.is_super_admin,
      hasAppAccess: teams.length > 0,
      // A local mock must not depend on v3 `/me` membership being complete.
      // A selected team is the mock's active context, so any other mocked
      // grant derives roster access there. The real mapper retains its
      // stricter membership check.
      isMemberOfActiveTeam: activeTeamTwrn != null,
    });
  });

  function setV3GrantsOverride(draft: PermissionDraftMap | null) {
    v3GrantsOverride.value = draft ? { ...draft } : null;
  }

  return {
    legacyGrants,
    v3Grants,
    hasAppAccess,
    setV3GrantsOverride,
  };
});
